import { FC, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { useLocale } from 'next-intl'
import clsx from 'clsx'
import WarrantyQueryForm from './warranty-query-form'
import WarrantyReportForm, { IssuePrefillValues } from './warranty-report-form'
import {
  getIssueInfoService,
  getRobotInfoService,
  RobotInfo,
  sendCheckEmailService,
  submitIssueFeedbackService,
  SubmitIssueFeedbackPayload,
} from '@/service/fault-reporting.service'

export interface WarrantyInfoProps {
  // 组件不需要外部 props
}

type QueryState = 'initial' | 'form'

const snRegex = /^[0-9a-zA-Z]{13,16}$/
const macRegex = /^[a-fA-F0-9:]{17}$/

function getRobotCategoryImageUrl(robotCategory?: string) {
  const category = (robotCategory || '').trim()
  if (!category) {
    return ''
  }
  return `https://pudu-link.oss-cn-hongkong.aliyuncs.com/robot-image-resource/small-size/${category}.png`
}

function buildLogDir(errorCode: string, time: string) {
  const prefixDir = '/sdcard/pudu/log/errorDiagnoses'
  return `${prefixDir}/${errorCode}_${time || ''}`
}

function buildErrors(errorsStr: string) {
  let errors: Array<{ errorCode?: string; errorDesc?: string; time?: string }> = []
  try {
    errors = errorsStr ? JSON.parse(errorsStr) : []
    if (errors.length > 1) {
      errors = errors.map((item, index) => ({
        errorCode: item.errorCode ? `${index + 1}. ${item.errorCode}` : undefined,
        errorDesc: item.errorDesc ? `${index + 1}. ${item.errorDesc}` : undefined,
        time: item.time,
      }))
    }
    const errorCodes = errors
      .map((item) => item.errorCode)
      .filter(Boolean)
      .join(' ')
    const errorDescs = errors
      .map((item) => item.errorDesc)
      .filter(Boolean)
      .join(' ')
    const logDirs = errors
      .map((item) => buildLogDir(item.errorCode || '', item.time || ''))
      .filter(Boolean)
      .join(' ')
    return { errorCodes, errorDescs, logDirs }
  } catch (error) {
    console.error('buildErrors error', error)
    return { errorCodes: '', errorDescs: '', logDirs: '' }
  }
}

function parseCosSignature(signatureString: string) {
  const params: Record<string, string> = {}
  const pairs = signatureString.split('&')
  pairs.forEach((pair) => {
    const [key, value] = pair.split('=')
    if (key && value !== undefined) {
      params[key] = decodeURIComponent(value)
    }
  })
  return params
}

const WarrantyInfo: FC<WarrantyInfoProps> = () => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const initializedRef = useRef(false)

  const [queryState, setQueryState] = useState<QueryState>('initial')
  const [snOrMac, setSnOrMac] = useState<string>('')
  const [robotInfo, setRobotInfo] = useState<RobotInfo | null>(null)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isQuerying, setIsQuerying] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [prefillValues, setPrefillValues] = useState<IssuePrefillValues>({
    errorCodes: '',
    errorDescs: '',
    logDirs: '',
    logUrl: '',
  })

  const handleQuerySubmit = async (value: string) => {
    const inputValue = value.trim()
    setSnOrMac(inputValue)
    setQueryError(null)
    setSubmitError(null)

    setIsQuerying(true)
    try {
      const response = await getRobotInfoService({ pidOrMac: inputValue }, locale)
      if (response.code === 200 && response.data) {
        setRobotInfo(response.data)
        setQueryState('form')
      } else {
        setQueryError(t('FaultReportingForm.QueryFailed'))
      }
    } catch (error) {
      console.error('query robot failed', error)
      setQueryError(t('FaultReportingForm.NetworkError'))
    } finally {
      setIsQuerying(false)
    }
  }

  useEffect(() => {
    if (!router.isReady || initializedRef.current) {
      return
    }
    initializedRef.current = true

    const sn = decodeURIComponent(String(router.query.sn || ''))
    const mac = decodeURIComponent(String(router.query.mac || ''))
    const errors = decodeURIComponent(String(router.query.errors || ''))
    const logUrl = decodeURIComponent(String(router.query.logUrl || ''))
    const logId = decodeURIComponent(String(router.query.logId || ''))

    const fromQuery = buildErrors(errors)
    setPrefillValues({
      ...fromQuery,
      logUrl,
    })

    if (logId) {
      getIssueInfoService({ uuid: logId }, locale)
        .then((res) => {
          if (res.code !== 200 || !res.data) {
            return
          }
          const parsed = parseCosSignature(res.data)
          const fallback = buildErrors(parsed.errors || '')
          setPrefillValues((prev) => ({
            ...prev,
            errorCodes: fallback.errorCodes || prev.errorCodes,
            errorDescs: fallback.errorDescs || prev.errorDescs,
            logDirs: fallback.logDirs || prev.logDirs,
            logUrl: parsed.logUrl || prev.logUrl,
          }))
        })
        .catch((error) => {
          console.error('prefill by logId failed', error)
        })
    }

    const pidOrMac = snRegex.test(sn) ? sn : macRegex.test(mac) ? mac : ''
    if (pidOrMac) {
      handleQuerySubmit(pidOrMac)
    }
  }, [locale, router.isReady, router.query.errors, router.query.logId, router.query.logUrl, router.query.mac, router.query.sn])

  const handleReset = () => {
    setQueryState('initial')
    setSnOrMac('')
    setRobotInfo(null)
    setQueryError(null)
    setSubmitError(null)
  }

  const handleSendEmailCode = async (email: string) => {
    try {
      const response = await sendCheckEmailService({ email }, locale)
      if (response.code === 200) {
        return true
      }
      setSubmitError(response.message || t('FaultReportingForm.SendCodeFailed'))
      return false
    } catch (error) {
      console.error('send code failed', error)
      setSubmitError(t('FaultReportingForm.NetworkError'))
      return false
    }
  }

  const handleFormSubmit = async (data: SubmitIssueFeedbackPayload) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const result = await submitIssueFeedbackService(data, locale)
      if (result.code === 200) {
        router.push('/support/fault-reporting/success')
      } else {
        setSubmitError(result.message || t('FaultReportingForm.SubmitFailed'))
      }
    } catch (error) {
      console.error('submit issue feedback failed', error)
      setSubmitError(t('FaultReportingForm.NetworkError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={clsx('w-full p-6 laptop:p-0')}>
      <div
        className={clsx(
          'w-full',
          'flex flex-col laptop:flex-row',
          'gap-6 laptop:gap-0',
          'laptop:justify-between',
          'items-start'
        )}
      >
        <h2
          className={clsx(
            'w-full laptop:w-[320px]',
            'text-24 laptop:text-36',
            'font-semibold',
            'text-black/90',
            'whitespace-normal break-words',
            'flex-shrink-0',
            'laptop:sticky laptop:top-30'
          )}
        >
          {t('FaultReportingForm.WarrantyInfo')}
        </h2>

        <div className={clsx('w-full', 'laptop:w-200', 'laptop:flex-shrink-0')}>
          {queryState === 'initial' && (
            <WarrantyQueryForm
              onSubmit={handleQuerySubmit}
              isLoading={isQuerying}
              error={queryError}
            />
          )}

          {queryState === 'form' && (
            <WarrantyReportForm
              sn={robotInfo?.robotSn || ''}
              mac={robotInfo?.robotMac || ''}
              productName={robotInfo?.robotCategory}
              productImage={getRobotCategoryImageUrl(robotInfo?.robotCategory)}
              initialValues={prefillValues}
              onSubmit={handleFormSubmit}
              onSendEmailCode={handleSendEmailCode}
              onResetInput={handleReset}
              isLoading={isSubmitting}
            />
          )}

          {submitError && (
            <p className={clsx('mt-3', 'text-[#F52242]', 'text-14 laptop:text-16')}>
              {submitError}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default WarrantyInfo
